import { isSubdomainLocked, getActiveTenantSlug, getKnownTenantPlatform } from '../utils/tenant';
import { clearAuth, isAuthenticated, isOnAuthCallback, refreshSession, tenantSlugFromToken, tokenExpiresSoon, tokensShareIdentity } from '../utils/auth';
import { dbgI } from '../utils/debug';

const COMP = 'api';

const BASE = '';
const isPWA = typeof window !== 'undefined'
  && (window.matchMedia('(display-mode: standalone)').matches
      || (navigator as unknown as { standalone?: boolean }).standalone === true);

export interface ApiFetchExtras {
  /**
   * When true, a 401 that is resolved via the structured user_deleted/
   * forced_logout codes still redirects immediately (that always wins,
   * regardless of this flag — see apiFetch's 401 branch). For every other
   * 401 shape, this only changes behavior BEFORE reactive refresh has been
   * attempted: none currently, since apiFetch always tries refreshSession()
   * first regardless of this flag. Once that refresh has been tried and
   * failed, the session is genuinely gone and apiFetch redirects to /login
   * unconditionally — this flag does NOT suppress that redirect. Existing
   * callers all pass this for background/polling calls (Navbar's tenant
   * list, App's profile fetch, etc.) and already treat any 401 as
   * non-fatal via a no-op .catch(), so redirecting once the session is
   * truly dead is strictly better than leaving a half-broken background
   * widget on screen. Kept for documentation/future-use rather than
   * removed outright — a future caller that genuinely wants to swallow a
   * 401 with zero side effects (no redirect at all) would need new,
   * explicit opt-out plumbing, not this flag as it stands today.
   */
  skipLogoutOn401?: boolean;
  /**
   * When true, return the raw Response object instead of the parsed JSON body.
   * Used for binary downloads (blob, PDF/CSV export) where the caller needs
   * to read the body itself. The 401 refresh-and-retry logic still applies,
   * so these calls get the same proactive/reactive refresh as normal JSON
   * callers — they just can't use apiFetch for the parse step.
   */
  raw?: boolean;
}

export async function apiFetch<T>(path: string, options: RequestInit & ApiFetchExtras = {}): Promise<T> {
  // Proactive refresh: if the access token is within 60s of expiry, refresh
  // BEFORE sending the request so we don't waste a round trip on a 401.
  // refreshSession() is a shared singleton — concurrent callers join the same
  // in-flight request. Safe to call on every apiFetch.
  if (!isOnAuthCallback() && tokenExpiresSoon()) {
    await refreshSession();
  }

  const token = localStorage.getItem('responsekit_token');
  // FormData uploads must NOT have a Content-Type — the browser sets it with
  // the correct multipart boundary. Hardcoding application/json would break
  // file uploads. Detect and drop.
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    'X-RK-Version': __APP_VERSION__,
    'X-RK-Client': isPWA ? 'pwa' : 'browser',
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  // On root domain, send X-Tenant header so backend resolves the right schema
  if (!isSubdomainLocked()) {
    const slug = getActiveTenantSlug();
    if (slug) {
      headers['X-Tenant'] = slug;

      // Dev-only: on a real subdomain (*.patrolschedule.com / *.responsekit.net)
      // isSubdomainLocked() is true and this whole block is skipped, so Host-based
      // resolution alone drives platform there, same as prod. Here (root domain /
      // localhost via the Vite proxy) the outgoing Host header always gets rewritten
      // to the proxy target (changeOrigin:true), so the backend can't tell platform
      // from Host. Send X-Platform-Override for the small set of tenants whose
      // platform we know client-side; the backend only honors it when
      // allow_platform_override is explicitly set AND app_env != production, so
      // this is inert everywhere except an opted-in dev/test backend. Unknown
      // slugs deliberately fall back to today's Host-only resolution rather than
      // guessing a platform.
      const platform = getKnownTenantPlatform(slug);
      if (platform) headers['X-Platform-Override'] = platform;
    }
  }

  const res = await fetch(`${BASE}${path}`, { ...options, headers });

  if (res.status === 401) {
    // On /auth/callback the session is still being established by
    // AuthCallback: any token this request carried is LEFTOVER state from a
    // previous session, and the code exchange is in flight. A 401 here must
    // not refresh, log out or navigate -- that is what moved the page to
    // /login mid-exchange and orphaned the flow's state (issue #1469). The
    // proactive refresh above is suppressed here for the same reason.
    // Reject like any other failed request, so the caller's .catch() runs
    // and nothing else happens. Once AuthCallback navigates off the
    // callback route, a 401 takes the normal path below.
    if (isOnAuthCallback()) {
      dbgI(COMP, '401 on /auth/callback — ignored (code exchange in flight)', { path });
      throw new Error('Unauthorized (sign-in in progress)');
    }

    // The session changed while this request was in flight (issue #1469,
    // IAM review L1): the 401 is about the token we SENT, not the session we
    // hold now. Typical case: a mount-time fetch on /auth/callback carried a
    // leftover token, AuthCallback then saved the fresh session and navigated
    // away, and the leftover token's 401 (possibly forced_logout) arrives
    // late -- acting on it would wipe the fresh session. So no refresh, no
    // clearAuth, no navigation on this path.
    //
    // Replay ONCE with the current token only when it is a ROTATION of the
    // same session (a refresh completed in flight): same user, same tenant,
    // same subtenant (tokensShareIdentity, fail-closed on anything
    // undecodable), and the X-Tenant this request was sent with still matches
    // both the current token's tenant and the active tenant. Anything else --
    // a different user on a shared tablet, a tenant or subtenant switch --
    // would replay user A's method + body under another identity or context
    // (confused deputy), so it just throws.
    const currentToken = localStorage.getItem('responsekit_token');
    if (currentToken !== token) {
      const sentXTenant = headers['X-Tenant'] ?? null;
      const tenantStillMatches = sentXTenant === null || (
        currentToken !== null
        && sentXTenant === tenantSlugFromToken(currentToken)
        && (isSubdomainLocked() || getActiveTenantSlug() === sentXTenant)
      );
      if (currentToken && tokensShareIdentity(token, currentToken) && tenantStillMatches) {
        dbgI(COMP, '401 for a rotated token — retrying once with the current token', { path });
        headers['Authorization'] = `Bearer ${currentToken}`;
        const retry = await fetch(`${BASE}${path}`, { ...options, headers });
        if (retry.ok) {
          if (options.raw) return retry as unknown as T;
          if (retry.status === 204) return null as T;
          const text = await retry.text();
          return text ? JSON.parse(text) : null as T;
        }
        throw new Error(`Request failed after the session changed (${retry.status})`);
      }
      dbgI(COMP, '401 for a token that is no longer current (different session) — not acting on it', { path });
      throw new Error('Unauthorized (session changed during the request)');
    }

    // Peek at the body to see if this is a structured signal from the
    // backend. claims_stale → silent refresh. user_deleted → drop the
    // session immediately; refreshing would just mint another dead token.
    //
    // This dict-shape lookup is load-bearing — the backend must preserve it.
    // See backend/app/main.py's StarletteHTTPException handler
    // (isinstance(exc.detail, dict) passthrough, no code-key gate) and
    // backend/app/auth.py:117-121,242-246,260-264,271-275 (the 4 call sites
    // that raise this exact {"code": ..., "message": ...} shape).
    let authCode: string | undefined;
    try {
      const cloned = res.clone();
      const body = await cloned.json().catch(() => null);
      authCode = body && typeof body === 'object' ? (body as { detail?: { code?: string } }).detail?.code : undefined;
      if (authCode === 'claims_stale') {
        dbgI(COMP, 'claims_stale 401 — silent refresh');
      } else if (authCode === 'user_deleted') {
        dbgI(COMP, 'user_deleted 401 — clearing session');
      } else if (authCode === 'forced_logout') {
        dbgI(COMP, 'forced_logout 401 — kill switch, clearing session');
      }
    } catch {
      // ignore
    }

    if (authCode === 'user_deleted' || authCode === 'forced_logout') {
      // user_deleted: account removed/deactivated server-side.
      // forced_logout: admin kill switch flipped for this tenant or the
      // whole service. In both cases refresh is worse than useless — a
      // new access token would be minted with a fresh iat against the
      // same condition and fail identically.
      const reason = authCode === 'forced_logout' ? 'session_terminated' : 'account_removed';
      clearAuth();
      window.location.href = `/login?reason=${reason}`;
      // Never resolve or reject — see the matching comment on the plain
      // expired-token fallthrough below. The reason is carried in the URL
      // query string for the login page itself, not via this rejection, so
      // nothing downstream needs this promise to settle.
      return new Promise<T>(() => {});
    }

    // Reactive refresh: token may have expired mid-flight or been revoked.
    // Shared singleton — joins any in-flight refresh (e.g. from a parallel
    // call that also 401'd) so we never issue two POST /api/auth/refresh.
    const refreshed = await refreshSession();

    if (refreshed) {
      // Retry the original request with the new token
      const newToken = localStorage.getItem('responsekit_token');
      if (newToken) headers['Authorization'] = `Bearer ${newToken}`;
      const retry = await fetch(`${BASE}${path}`, { ...options, headers });
      if (retry.ok) {
        if (options.raw) return retry as unknown as T;
        if (retry.status === 204) return null as T;
        const text = await retry.text();
        return text ? JSON.parse(text) : null as T;
      }
      // Retry also failed — fall through to logout
    }

    // A truly expired/invalid session is routine, expected state, not an
    // error — the user should land on the login form with no error UI ever
    // rendered, not a flashed error message or raw JSON while the redirect
    // is in flight. This applies even to skipLogoutOn401 background calls
    // (e.g. Navbar's tenant-list poll): skipLogoutOn401 exists to stop a
    // transient 401 on ONE background call from logging out a session that's
    // still valid elsewhere, but once refreshSession() has already been
    // tried above and failed, the session is genuinely gone — there's
    // nothing left to preserve by staying on the page.
    const expiredToken = localStorage.getItem('responsekit_token');
    if (expiredToken) {
      fetch(`${BASE}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${expiredToken}`, 'Content-Type': 'application/json' },
      }).catch(() => {});
    }
    clearAuth();
    window.location.href = '/login';
    // Never resolve or reject — navigation is already underway. Letting the
    // promise settle would race the redirect: a caller's .then()/.catch()
    // could still run and briefly render an error/empty state (or, per the
    // regression this fixes, the raw error body) before the browser actually
    // leaves the page.
    return new Promise<T>(() => {});
  }

  if (!res.ok) {
    // Read the body once as text, then try to parse it as JSON.
    // Attempting res.json() first consumes the stream; a failed parse then
    // leaves res.text() returning empty string, so the body text is lost.
    const bodyText = await res.text().catch(() => '');
    let detail: string;
    try {
      const err = JSON.parse(bodyText);
      detail = typeof err.detail === 'string' ? err.detail : JSON.stringify(err);
    } catch {
      // Not JSON — surface the raw body text (e.g. "Service Unavailable")
      detail = bodyText || res.statusText || `HTTP ${res.status}`;
    }
    console.error(`[API] ${options.method || 'GET'} ${path} → ${res.status}: ${detail}`);
    throw new Error(detail);
  }
  // raw mode: caller wants the Response itself (blob, text, stream, etc.)
  // Must bypass the JSON parser. 401 refresh-and-retry already happened above.
  if (options.raw) {
    return res as unknown as T;
  }
  // 204 No Content or empty body — return null (e.g. DELETE responses)
  if (res.status === 204) {
    return null as T;
  }
  const text = await res.text();
  if (!text) {
    return null as T;
  }
  try {
    return JSON.parse(text);
  } catch (parseErr) {
    console.error(`[API] ${options.method || 'GET'} ${path} → JSON parse error (body: ${text.slice(0, 200)}):`, parseErr);
    throw new Error(`Invalid JSON response from ${path}`);
  }
}

/**
 * Open a bearer-token-authenticated API URL (file preview/download) in a new
 * tab, e.g. `<a target="_blank" href="/api/.../file">`.
 *
 * These links are a real, browser-level top-level navigation, not a fetch()
 * — a token embedded in the URL that the backend rejects for ANY reason
 * (expired, revoked, tampered, signing-key rotated, tenant/claims mismatch)
 * renders the backend's raw JSON error body ({"detail":"..."}) as the ENTIRE
 * page with no React around it. No error boundary can catch this class of
 * bug — React never runs on that page at all.
 *
 * The local exp-claim checks below (isAuthenticated/tokenExpiresSoon) only
 * catch a token that LOOKS expired client-side; they cannot catch a token
 * that still looks locally valid but the backend rejects anyway. That gap is
 * exactly what let the raw JSON regression through. To close it for every
 * rejection reason, not just local-clock expiry, this does a real
 * apiFetch() validation call (GET /api/auth/profile, skipLogoutOn401 so a
 * transient failure here doesn't log out a session that's fine elsewhere)
 * before ever opening the tab — apiFetch's own 401 handling already
 * guarantees a silent redirect-to-login with no error UI on genuine
 * rejection, and a promise that never resolves in that case, so this
 * function simply never reaches window.open() when the session is bad.
 */
export async function openAuthedFile(path: string): Promise<void> {
  // isAuthenticated() is false for BOTH missing and already-expired tokens;
  // tokenExpiresSoon() covers the not-yet-expired-but-about-to-be case (it
  // deliberately returns false once exp has actually passed, per its own
  // doc comment) — the two together cover every case that needs a refresh.
  if (!isAuthenticated() || tokenExpiresSoon()) {
    const refreshed = await refreshSession();
    if (!refreshed) {
      // Refresh failed: either an authoritative 401 rejection (refreshSession()
      // already cleared auth) or a transient 5xx/429/network failure (storage
      // is deliberately kept, #1508). Either way, send the whole app to login
      // instead of opening a doomed tab, matching apiFetch's own behavior on
      // an unrecoverable auth failure.
      window.location.href = '/login';
      return;
    }
  }
  // Validate the (now locally-fresh-looking) token actually works
  // server-side before trusting it in a top-level navigation. On a genuine
  // rejection apiFetch() redirects to /login itself and this call never
  // resolves, so nothing after it — including window.open() — ever runs.
  await apiFetch('/api/auth/profile', { skipLogoutOn401: true });
  const token = localStorage.getItem('responsekit_token') || '';
  const sep = path.includes('?') ? '&' : '?';
  window.open(`${path}${sep}token=${encodeURIComponent(token)}`, '_blank', 'noopener,noreferrer');
}
